import type { LabelGroups } from '../util';

import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api } from '../../../api';
import { EmptyMessage, Text } from '../../../components';
import { Style } from '../../../constants';
import TicketTableRow from './TicketTableRow';

type Props = {
  tickets: ReadonlyArray<Api.Ticket.V_List>;
  companySlug: string;
  labelGroups: LabelGroups;
  isLoading: boolean;
  searchQuery?: string;
  invalidate: () => unknown;
};

const COLUMNS = [
  { label: 'No.', style: 'colNo' as const },
  { label: 'Ticket', style: 'colTicket' as const },
  { label: 'Type', style: 'colType' as const, hideOnMobile: true },
  { label: 'Platform', style: 'colPlatform' as const, hideOnMobile: true },
  { label: 'Status', style: 'colStatus' as const },
  { label: 'Client', style: 'colClient' as const, hideOnMobile: true },
  { label: 'M10c', style: 'colM10c' as const, hideOnMobile: true },
];

export default function TicketTable({
  tickets,
  companySlug,
  labelGroups,
  searchQuery,
  isLoading,
  invalidate,
}: Props) {
  if (isLoading) {
    return <EmptyMessage>Loading tickets...</EmptyMessage>;
  }
  if (tickets.length === 0) {
    return <EmptyMessage>No matching tickets</EmptyMessage>;
  }

  return (
    <html.div style={styles.container}>
      <html.div style={[Style.row, Style.alignCenter, styles.header]}>
        {COLUMNS.map((col) => (
          <html.div
            key={col.label}
            // @ts-expect-error typescript issue with conditionals
            style={[
              styles.headerCell,
              styles[col.style],
              col.hideOnMobile && styles.hideOnMobile,
            ]}
          >
            <Text typeface="body.small.regular" color="neutral80">
              {col.label}
            </Text>
          </html.div>
        ))}
      </html.div>
      <html.div>
        {tickets.map((ticket) => (
          <TicketTableRow
            key={ticket.id}
            ticket={ticket}
            companySlug={companySlug}
            labelGroups={labelGroups}
            searchQuery={searchQuery}
            invalidate={invalidate}
          />
        ))}
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  container: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: color.neutral10,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
  },
  headerCell: {
    padding: 12,
  },
  colNo: {
    width: 64,
    flexShrink: 0,
  },
  colTicket: {
    flex: 1,
    minWidth: 0,
  },
  colType: {
    width: 128,
    flexShrink: 0,
  },
  colPlatform: {
    width: 121,
    flexShrink: 0,
  },
  colStatus: {
    width: 176,
    flexShrink: 0,
  },
  colClient: {
    width: 146,
    flexShrink: 0,
  },
  colM10c: {
    width: 146,
    flexShrink: 0,
  },
  hideOnMobile: {
    '@media (max-width: 1000px)': {
      display: 'none',
    },
  },
});
